import { useCallback } from 'react';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import { REFRESH_TOKEN_LIFETIME_MS, readSdkRefreshToken } from 'utils/helpers/auth0Session.helpers';
import useKeystoreStorageHelpers from 'utils/helpers/keystoreStorage.helpers';
import {
  derivePinIdentity,
  deriveWrapKeyFromPin,
  generateSalt,
  toBase64,
  wipe,
  wrap,
} from 'utils/helpers/pinCrypto.helpers';
import { toPinAttemptFailure } from 'utils/helpers/pinUnlock.helpers';
import {
  getCachedPinKey,
  pinKeyEntryToBlob,
  runKeystoreMutation,
  setCachedPinKey,
  setPinLockout,
} from 'utils/helpers/pinKeyCache.helpers';
import { saveSessionWrapKey } from 'utils/helpers/pinSessionKey.helpers';
import { usePinOrgId } from './usePinOrgId';

// Sets (or replaces) the signed-in user's PIN: wraps their current refresh token under a key derived
// from the PIN + the pepper, and stores the result as pin.key (keystore + local cache).
export const useEnrolPin = () => {
  const { data: user } = useCurrentUser();
  const orgId = usePinOrgId();
  const { constructPinKeyPath, uploadPinKey } = useKeystoreStorageHelpers();

  return useCallback(
    async (pin: string): Promise<void> => {
      const refreshToken = readSdkRefreshToken();
      if (!user || !orgId || !refreshToken) throw new Error('A password session is required to set up a PIN');

      const salt = generateSalt();
      try {
        const pinIdentity = await derivePinIdentity(user.email, orgId);
        const key = await deriveWrapKeyFromPin(pinIdentity, pin, salt);
        const { iv, ciphertext } = await wrap(key, refreshToken);

        const entry = {
          userId: user.userId,
          pinKeyPath: constructPinKeyPath({
            tenantId: user.tenant.id,
            locationId: user.location.locationId,
            userId: user.userId,
          }),
          expiryEpochMs: Date.now() + REFRESH_TOKEN_LIFETIME_MS,
          tokenStore: { salt: toBase64(salt), iv, ciphertext },
        };

        // the local cache is enough to log in on this device; the keystore upload continues in the
        // background and, if interrupted, is retried from the pendingUpload flag (useFlushPendingPinKey)
        setCachedPinKey(orgId, user.email, { ...entry, pendingUpload: true });
        await saveSessionWrapKey(orgId, user.email, key, entry.tokenStore.salt, refreshToken);
        // the entry may have been removed or replaced meanwhile; upload only what is still current
        runKeystoreMutation(entry.pinKeyPath, async () => {
          const latest = getCachedPinKey(orgId, user.email);
          if (latest?.tokenStore.ciphertext !== ciphertext || latest.pendingDelete) return;
          await uploadPinKey(entry.pinKeyPath, pinKeyEntryToBlob(latest));
        })
          .then(() => {
            const current = getCachedPinKey(orgId, user.email);
            if (current?.tokenStore.ciphertext === ciphertext) {
              setCachedPinKey(orgId, user.email, { ...current, pendingUpload: false });
            }
          })
          .catch(() => undefined);
      } catch (error) {
        const failure = toPinAttemptFailure(error);
        if (failure?.status === 'locked_out') {
          setPinLockout(orgId, user.email, failure.retryAfterSeconds, user.userId);
        }
        throw error;
      } finally {
        wipe(salt);
      }
    },
    [user, orgId, constructPinKeyPath, uploadPinKey],
  );
};
